import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getPrimaryProductImage } from "../../services/productImageService";
import { formatCurrencyFromCzk } from "../../utils/prices";
import { czk_eur_rate } from "../../constants/prices";
import QuantityControl from "../QuantityControl/QuantityControl";
import styles from "./CartProductCard.module.css";

import { Product, Package } from "../../types/product.types";
import { StorageItem } from "../../types/storageItem.types";
import { Activity } from "../../types/activity.types";
import { CartItem } from "../../types/cart.types";

type AnyProduct = Product | Package | StorageItem | Activity;

interface CartProductCardProps {
    cartItem: CartItem;
    product: AnyProduct;
    selectedCurrency: string;
    onRemove: () => void;
    onAmountChange: (newAmount: number) => void;
    className?: string;
}

// Type guards
const isActivity = (p: AnyProduct): p is Activity => "title" in p;
const hasGranularity = (p: AnyProduct): p is Product | Package =>
    "granularity" in p;
const hasPricing = (
    p: AnyProduct
): p is Product | Package | StorageItem =>
    "pricing" in p && p.pricing !== undefined;

// Get display name based on language
const getDisplayName = (product: AnyProduct, lang: string): string => {
    if (isActivity(product)) {
        return lang === "en" ? product.title_en || "N/A" : product.title;
    }
    const p = product as Product | Package | StorageItem;
    return lang === "en" ? (p as any).name_en || "N/A" : p.name;
};

// Get product image URL
const getImageUrl = (product: AnyProduct): string => {
    if (isActivity(product)) {
        return getPrimaryProductImage(product);
    }
    return getPrimaryProductImage(product as any);
};

// Get granularity value
const getGranularity = (product: AnyProduct, category: string): number => {
    // Activities and storage items use granularity of 1
    if (category === "activities" || category === "storage") return 1;
    if (hasGranularity(product)) return product.granularity ?? 1;
    return 1;
};

// Get max order value
const getMaxOrder = (product: AnyProduct, category: string): number => {
    // Activities are capped at 1
    if (category === "activities") return 1;
    if ("max_order" in product) {
        const maxOrder = (product as any).max_order;
        return maxOrder === null || maxOrder === 0 ? 100 : maxOrder;
    }
    return 100;
};

// Get price without tax
const getPriceWithoutTax = (product: AnyProduct): number | null => {
    if (isActivity(product)) return null; // Activities don't have pricing
    if (hasPricing(product)) {
        return product.pricing?.price_without_tax ?? null;
    }
    return null;
};

const CartProductCard = ({
    cartItem,
    product,
    selectedCurrency,
    onRemove,
    onAmountChange,
    className,
}: CartProductCardProps) => {
    const { i18n, t } = useTranslation();

    const displayName = getDisplayName(product, i18n.language);
    const imageUrl = getImageUrl(product);
    const granularity = getGranularity(product, cartItem.category);
    const maxOrder = getMaxOrder(product, cartItem.category);
    const priceWithoutTax = getPriceWithoutTax(product);

    const handleDecrease = () => {
        const next = cartItem.amount - granularity;
        onAmountChange(Math.max(0, next));
    };

    const handleIncrease = () => {
        onAmountChange(cartItem.amount + granularity);
    };

    return (
        <div className={`${styles.CartProduct} ${className ?? ""}`}>
            <div
                className={styles.CartProductImage}
                style={{ backgroundImage: `url(${imageUrl})` }}
            />
            <div className={styles.CartProductContent}>
                <div className={styles.CartProductInfo}>
                    <p className={styles.CartProductTitle}>{displayName}</p>
                    <div className={styles.CartProductButtons}>
                        <button
                            className={styles.CartProductButton}
                            onClick={onRemove}
                        >
                            <X />
                        </button>
                    </div>
                </div>

                <div className={styles.CartProductOrderInfo}>
                    <QuantityControl
                        amount={cartItem.amount}
                        onDecrease={handleDecrease}
                        onIncrease={handleIncrease}
                        disableIncrease={cartItem.amount >= maxOrder}
                    />

                    {priceWithoutTax !== null && (
                        <p className={styles.CartProductPrice}>
                            {t("products.no_dph_short")}{" "}
                            {formatCurrencyFromCzk(
                                priceWithoutTax * cartItem.amount,
                                czk_eur_rate,
                                selectedCurrency
                            )}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CartProductCard;
